// src/whatsappChat/api/client.js
import axios from 'axios';
import { API_URL } from '../../config';

// Same prefix convention as the rest of the CRM: `${API_URL}<module>/...`
export const WA_CHAT_BASE_URL = `${API_URL}botsense-chat`;

// socket.io namespace lives on the API host itself (no trailing slash)
export const WA_CHAT_SOCKET_URL = `${String(API_URL || '').replace(/\/+$/, '')}/botsense-chat`;

export const getAuthToken = () => localStorage.getItem('authToken');

export const waChatClient = axios.create({
  baseURL: WA_CHAT_BASE_URL,
});

waChatClient.interceptors.request.use((config) => {
  const token = getAuthToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

/**
 * Unread totals for the CRM sidebar badge: { chats, messages, by_number }.
 * Never 403s (no chat access = zeros). `numberId` limits it to one business number.
 */
export async function fetchWaUnreadCount(numberId) {
  const res = await waChatClient.get('/conversations/unread-count', {
    params: numberId ? { number_id: numberId } : undefined,
  });
  return res.data?.data || { chats: 0, messages: 0, by_number: [] };
}

// Window event the sidebar listens to, so its badge refetches after the
// module sees new messages or marks a chat as read.
export const WA_UNREAD_CHANGED_EVENT = 'waChatUnreadChanged';

export const notifyWaUnreadChanged = () => {
  window.dispatchEvent(new Event(WA_UNREAD_CHANGED_EVENT));
};

/**
 * Turns either error shape into { status, code, message }.
 *   ours:     { statusCode, message, error }
 *   BotSense: { success: false, source: "botsense", code, message, request_id }
 */
export function sendErrorMessage(err) {
  const e = normalizeError(err);
  // No HTTP answer at all: the request may still have gone through.
  if (!e.status) {
    return 'No response from the server. The message may still have been sent. Check the chat before sending it again.';
  }
  if (e.code === 'tsp_media_upload_not_supported') {
    return 'This business number cannot upload files.';
  }
  return e.message;
}

export function normalizeError(err) {
  const data = err?.response?.data;
  const status = err?.response?.status ?? null;
  let message = data?.message;
  if (Array.isArray(message)) message = message.join(', ');

  return {
    status,
    code: data?.code ?? null,
    fromBotsense: data?.source === 'botsense',
    requestId: data?.request_id ?? null,
    message:
      message ||
      (status ? 'Something went wrong. Please try again.' : 'Could not reach the server.'),
  };
}
